import { z } from "zod";

/**
 * Rago Discovery DTOs — the normalized shape our API returns from its
 * server-side TMDB proxy (`/v1/discover/*`). Field names deliberately mirror
 * the SPA's app-local MediaItem so mapping in `discoverySource` stays
 * mechanical. Images are TMDB **paths** only; the browser loads them directly
 * from https://image.tmdb.org (the API never proxies image bytes).
 */

export const mediaTypeSchema = z.enum(["movie", "tv"]);
export type DiscoveryMediaType = z.infer<typeof mediaTypeSchema>;

export const discoveryItemSchema = z.object({
  id: z.string(), // TMDB id as string
  mediaType: mediaTypeSchema,
  title: z.string(),
  year: z.number().int().nullable(),
  overview: z.string(),
  posterPath: z.string().nullable(),
  backdropPath: z.string().nullable(),
  // Empty in list responses, except the hubs' lead item (Discover's hero);
  // populated on detail.
  genres: z.array(z.string()),
  // TV only: number of regular seasons (enriched from TMDB); null for movies/unknown.
  seasonCount: z.number().int().nullable(),
  // Present where the API looked the title up — the hubs' lead item (and,
  // required, on detail); absent on plain list items.
  runtimeMs: z.number().int().min(0).optional(),
  /** Person credits only: how the person figures on this title — "Actor"
   * and/or TMDB jobs ("Director", "Writer"), deduped, cast first. Absent on
   * hubs, search, detail and on payloads from pre-roles brokers. */
  roles: z.array(z.string()).optional(),
});
export type DiscoveryItem = z.infer<typeof discoveryItemSchema>;

/** A person named on a title with the id that opens their page. */
export const discoveryPersonRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  profilePath: z.string().nullable(),
});
export type DiscoveryPersonRef = z.infer<typeof discoveryPersonRefSchema>;

export const discoverySeasonSummarySchema = z.object({
  seasonNumber: z.number().int(),
  name: z.string(),
  episodeCount: z.number().int(),
  posterPath: z.string().nullable(),
  airYear: z.number().int().nullable(),
  overview: z.string(),
});
export type DiscoverySeasonSummary = z.infer<typeof discoverySeasonSummarySchema>;

export const discoveryHubSchema = z.object({
  id: z.string(),
  title: z.string(),
  items: z.array(discoveryItemSchema),
});
export type DiscoveryHub = z.infer<typeof discoveryHubSchema>;

export const discoveryHubsResponseSchema = z.object({
  rows: z.array(discoveryHubSchema),
});
export type DiscoveryHubsResponse = z.infer<typeof discoveryHubsResponseSchema>;

export const discoveryCastMemberSchema = z.object({
  id: z.string(),
  name: z.string(),
  character: z.string(),
  profilePath: z.string().nullable(),
});
export type DiscoveryCastMember = z.infer<typeof discoveryCastMemberSchema>;

export const discoveryDetailSchema = discoveryItemSchema.extend({
  runtimeMs: z.number().int().min(0),
  /** Movie: director; TV: creator. Null when TMDB doesn't list one. */
  director: z.string().nullable(),
  /** Movie: every crew member with job Director, TMDB order; TV: the
   * creators. `director` is directors[0]'s name. Optional so cached
   * payloads from pre-directors brokers still parse. */
  directors: z.array(discoveryPersonRefSchema).optional(),
  cast: z.array(discoveryCastMemberSchema),
  recommendations: z.array(discoveryItemSchema),
  trailerUrl: z.string().nullable(),
  // TV only:
  numberOfSeasons: z.number().int().nullable(),
  seasons: z.array(discoverySeasonSummarySchema),
  /** TMDB community score, 0–10; null when unrated. Optional so cached
   * payloads from pre-score brokers still parse. */
  voteAverage: z.number().nullable().optional(),
  /** TMDB's title logo (a transparent PNG, English first); null when the
   * title has none. Optional so cached payloads from pre-logo brokers still
   * parse. */
  logoPath: z.string().nullable().optional(),
});
export type DiscoveryDetail = z.infer<typeof discoveryDetailSchema>;

export const discoveryEpisodeSchema = z.object({
  episodeNumber: z.number().int(),
  name: z.string(),
  overview: z.string(),
  stillPath: z.string().nullable(),
  airDate: z.string().nullable(),
  runtimeMs: z.number().int().min(0),
  voteAverage: z.number(),
});
export type DiscoveryEpisode = z.infer<typeof discoveryEpisodeSchema>;

export const discoverySeasonResponseSchema = z.object({
  seasonNumber: z.number().int(),
  name: z.string(),
  episodes: z.array(discoveryEpisodeSchema),
});
export type DiscoverySeasonResponse = z.infer<typeof discoverySeasonResponseSchema>;

export const discoverySearchResponseSchema = z.object({
  items: z.array(discoveryItemSchema),
});
export type DiscoverySearchResponse = z.infer<typeof discoverySearchResponseSchema>;

export const discoveryPersonSchema = z.object({
  id: z.string(),
  name: z.string(),
  biography: z.string(),
  birthday: z.string().nullable(),
  deathday: z.string().nullable(),
  placeOfBirth: z.string().nullable(),
  knownForDepartment: z.string(),
  profilePath: z.string().nullable(),
  knownFor: z.array(discoveryItemSchema),
  credits: z.array(discoveryItemSchema),
});
export type DiscoveryPerson = z.infer<typeof discoveryPersonSchema>;
